import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

const SubscribersList = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, active, unsubscribed
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    // Real-time listener for Users collection
    const usersQuery = query(
      collection(db, "Users"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const subscribersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSubscribers(subscribersData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching subscribers:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = subscribers;

    // Filter by status
    if (filter === "active") {
      filtered = filtered.filter(s => s.subscribedToNewsletter && !s.isUnsubscribed);
    } else if (filter === "unsubscribed") {
      filtered = filtered.filter(s => s.isUnsubscribed);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.displayName && s.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredSubscribers(filtered);
    setPage(1); // Reset to first page when filters change
  }, [subscribers, filter, searchTerm]);

  const exportToCSV = () => {
    const csvData = [
      ["Email", "Name", "Subscribed", "Status", "Source", "Subscribed At", "Total Comments", "Total Likes"],
      ...filteredSubscribers.map(s => [
        s.email,
        s.displayName || "",
        s.subscribedToNewsletter ? "Yes" : "No",
        s.isUnsubscribed ? "Unsubscribed" : "Active",
        s.subscriptionSource || "",
        s.subscribedAt ? new Date(s.subscribedAt).toLocaleDateString() : "",
        s.totalComments || 0,
        s.totalLikes || 0
      ])
    ];

    const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  // Pagination
  const paginatedSubscribers = filteredSubscribers.slice(0, page * itemsPerPage);
  const hasMore = filteredSubscribers.length > paginatedSubscribers.length;

  // Stats
  const stats = {
    total: subscribers.length,
    active: subscribers.filter(s => s.subscribedToNewsletter && !s.isUnsubscribed).length,
    unsubscribed: subscribers.filter(s => s.isUnsubscribed).length,
    commenters: subscribers.filter(s => (s.totalComments || 0) > 0).length
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Subscribers Management</h2>
        <p className="text-gray-400">View and manage your newsletter subscribers</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4">
          <div className="text-gray-400 text-sm mb-1">Total Users</div>
          <div className="text-2xl font-bold text-white">{stats.total}</div>
        </div>
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-4">
          <div className="text-gray-400 text-sm mb-1">Active Subscribers</div>
          <div className="text-2xl font-bold text-white">{stats.active}</div>
        </div>
        <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="text-gray-400 text-sm mb-1">Unsubscribed</div>
          <div className="text-2xl font-bold text-white">{stats.unsubscribed}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-lg p-4">
          <div className="text-gray-400 text-sm mb-1">Active Commenters</div>
          <div className="text-2xl font-bold text-white">{stats.commenters}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
          />
        </div>

        {/* Filter */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 cursor-pointer"
        >
          <option value="all">All Users ({subscribers.length})</option>
          <option value="active">Active Subscribers ({stats.active})</option>
          <option value="unsubscribed">Unsubscribed ({stats.unsubscribed})</option>
        </select>

        {/* Export */}
        <button
          onClick={exportToCSV}
          className="px-6 py-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 hover:border-purple-500/50 transition-all flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Source</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Subscribed</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Activity</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSubscribers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  paginatedSubscribers.map((subscriber, index) => (
                    <motion.tr
                      key={subscriber.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3 text-white text-sm font-mono">{subscriber.email}</td>
                      <td className="px-4 py-3 text-gray-300 text-sm">{subscriber.displayName || "-"}</td>
                      <td className="px-4 py-3">
                        {subscriber.subscribedToNewsletter && !subscriber.isUnsubscribed ? (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full">
                            Subscribed
                          </span>
                        ) : subscriber.isUnsubscribed ? (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-semibold rounded-full">
                            Unsubscribed
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs font-semibold rounded-full">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm capitalize">
                        {subscriber.subscriptionSource?.replace(/_/g, " ") || "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {formatDate(subscriber.subscribedAt || subscriber.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        <div className="flex items-center gap-3 text-xs">
                          <span title="Comments">
                            💬 {subscriber.totalComments || 0}
                          </span>
                          <span title="Likes">
                            ❤️ {subscriber.totalLikes || 0}
                          </span>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center p-6 border-t border-white/10">
              <button
                onClick={() => setPage(page + 1)}
                className="px-6 py-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 hover:border-purple-500/50 transition-all"
              >
                Load More ({filteredSubscribers.length - paginatedSubscribers.length} remaining)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscribersList;
